import React, { useContext, useMemo } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
  WIDGET_KEY_TO_COMPONENT as widgetKeyMap,
  WIDGET_TYPE_TO_ICON as widgetTypeMap,
} from "../../CustomizedDashboard/Widgets/WidgetKeyMap";
import { useQuery } from "@apollo/client";
import { GET_WIDGET_CATEGORIES } from "../../CustomizedDashboard/DashboardApi";
import {
  CheckBox,
  TextButton,
  TextInput,
  SelectInput,
  useMutationWrapper,
  AlertIndicator,
  alertTypes,
  ModalContext,
  ModalButtonsContainer,
  showErrorDialog,
  REQUIRED_ERROR_MESSAGE,
} from "@SeemplicityDev/shared";
import {
  ADD_WIDGET_DEFINITION,
  type AddWidgetDefinitionData,
  type AddWidgetDefinitionVariables,
  type WidgetDefinitionInput,
} from "./LaunchPublicActionsApi";
import { showToast } from "../../Forms/Dialogs/Toast/Toast";

interface SelectOption {
  label: string;
  value: string;
}

interface NullableSelectOption {
  label: string;
  value: string | null;
}

interface WidgetCategoriesData {
  widgets_categories_definitions?: {
    edges?: Array<{
      node: {
        id: string;
        name: string;
      };
    }>;
  };
}

interface AddWidgetDefinitionFormValues {
  name: string;
  store_name: string;
  key: SelectOption;
  category: NullableSelectOption;
  chart_type: SelectOption;
  widget_description: string;
  widget_help_description: string;
  min_width: string;
  max_width: string;
  min_height: string;
  has_time_controller: boolean;
  is_clickable: boolean;
  is_common: boolean;
  vertical_legend: boolean;
  sla_widget: boolean;
  due_date_widget: boolean;
}

const AddWidgetDefinitionAction: React.FC = () => {
  const methods = useForm<AddWidgetDefinitionFormValues>({ mode: "all" });
  const {
    handleSubmit,
    control,
    formState: { errors, isValid, isDirty },
  } = methods;

  const { closeModal } = useContext(ModalContext);

  const { data: widgetCategoriesData } = useQuery<WidgetCategoriesData>(
    GET_WIDGET_CATEGORIES
  );
  // useMutationWrapper is untyped JS; cast its tuple result (house precedent).
  const [createWidgetDefinition] = useMutationWrapper(
    ADD_WIDGET_DEFINITION
  ) as unknown as [
    (args: {
      variables: AddWidgetDefinitionVariables;
    }) => Promise<{ data?: AddWidgetDefinitionData }>,
  ];

  const maxWidth = useWatch({
    control,
    name: `max_width`,
  });
  const minWidth = useWatch({
    control,
    name: `min_width`,
  });

  const keyOptions = useMemo(() => {
    return Object.keys(widgetKeyMap).map((key) => {
      return { label: key, value: key };
    });
  }, [widgetKeyMap]);

  const categoryOptions = useMemo(() => {
    if (!!widgetCategoriesData) {
      const { widgets_categories_definitions } = widgetCategoriesData;
      // Optional-chained `.edges?.map(...)` preserved verbatim from the original
      // JS: when edges is undefined the result is undefined and spreading it
      // throws at runtime. The cast keeps that exact expression/behavior rather
      // than masking it with a `?? []` fallback.
      return [
        { value: null, label: "No Category" },
        ...(widgets_categories_definitions?.edges?.map(({ node }) => ({
          label: node.name,
          value: node.id,
        })) as NullableSelectOption[]),
      ];
    }
  }, [widgetCategoriesData]);

  const typeOptions = useMemo(() => {
    return Object.keys(widgetTypeMap).map((key) => {
      return { label: key, value: key };
    });
  }, [widgetTypeMap]);

  const onSubmit = (data: AddWidgetDefinitionFormValues) => {
    const widgetDefinition: WidgetDefinitionInput = {
      key: data.key?.value,
      default_name_template: data.name,
      store_name_template: data.store_name,
      category_id: data.category?.value,
      chart_type: data.chart_type?.value,
      widget_description_template: data.widget_description,
      widget_help_description_template: data.widget_help_description ?? "",
      min_height: parseFloat(data.min_height),
      min_width: parseFloat(data.min_width),
      max_width: parseFloat(data.max_width),
      has_time_controller: !!data.has_time_controller,
      vertical_legend: !!data.vertical_legend,
      is_common: !!data.is_common,
      is_clickable: !!data.is_clickable,
      tags: [
        ...(!!data.sla_widget ? ["SLA"] : []),
        ...(!!data.due_date_widget ? ["TICKET_DUE_DATE"] : []),
      ],
    };

    createWidgetDefinition({ variables: { widgetDefinition } })
      .then((res) => {
        showToast({
          message: (
            <div className={"modal-text"}>
              The Widget Definition of{" "}
              <span className={"modal-live-text"}>{data.name}</span> has been
              created successfully.
            </div>
          ),
        });
        closeModal();
      })
      .catch((err) => {
        showErrorDialog({ message: err.message });
      });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className={"action-form-wrapper"}>
        <AlertIndicator
          type={alertTypes.WARNING}
          content={
            "Please make sure to add the Widget to both US and EU environments!"
          }
        />
        <Controller
          name={"name"}
          render={({ field: { onChange, onBlur, value } }) => (
            <TextInput
              placeholder={"Name of the Widget"}
              type={"text"}
              label={"Name of the Widget"}
              description={
                "Please note: if you add a severity/priority to the name use the {severity_field_name} template\n and if you want the name to include the current timeScale use the  ${{timeScale}} template"
              }
              required
              labelTop
              error={errors?.name ? errors.name : null}
              value={value}
              onChange={(val: React.ChangeEvent<HTMLInputElement>) =>
                onChange(val)
              }
            />
          )}
          control={control}
          rules={{ required: REQUIRED_ERROR_MESSAGE }}
        />
        <Controller
          name={"store_name"}
          render={({ field: { onChange, onBlur, value } }) => (
            <TextInput
              placeholder={"Name of the Widget in Store"}
              type={"text"}
              label={"Store Name of the Widget"}
              description={
                "Please note: if you add a severity/priority to the name use the {severity_field_name} template\n and if you want the name to include the current timeScale use the  {{period}} template"
              }
              required
              labelTop
              error={errors?.store_name ? errors.store_name : null}
              value={value}
              onChange={(val: React.ChangeEvent<HTMLInputElement>) =>
                onChange(val)
              }
            />
          )}
          control={control}
          rules={{ required: REQUIRED_ERROR_MESSAGE }}
        />
        <Controller
          name={"key"}
          render={({ field: { onChange, onBlur, value } }) => (
            <SelectInput
              label={"Key"}
              labelTop
              options={keyOptions}
              onChange={(selectedValue: SelectOption) =>
                onChange(selectedValue)
              }
              value={value}
              placeholder={"Select a key to Map Widget Component"}
              required
            />
          )}
          control={control}
          rules={{ required: REQUIRED_ERROR_MESSAGE }}
        />
        <Controller
          name={"category"}
          render={({ field: { onChange, onBlur, value } }) => (
            <SelectInput
              label={"Category"}
              labelTop
              options={categoryOptions}
              onChange={(selectedValue: NullableSelectOption) =>
                onChange(selectedValue)
              }
              value={value}
              placeholder={"Select a Widget Category"}
              required
            />
          )}
          control={control}
          rules={{ required: REQUIRED_ERROR_MESSAGE }}
        />
        <Controller
          name={"chart_type"}
          render={({ field: { onChange, onBlur, value } }) => (
            <SelectInput
              label={"Chart Type"}
              labelTop
              options={typeOptions}
              onChange={(selectedValue: SelectOption) =>
                onChange(selectedValue)
              }
              value={value}
              placeholder={"Select a Widget Chart Type"}
              required
            />
          )}
          control={control}
          rules={{ required: REQUIRED_ERROR_MESSAGE }}
        />
        <Controller
          name={"widget_description"}
          render={({ field: { onChange, onBlur, value } }) => (
            <TextInput
              placeholder={"Widget Description"}
              type={"text"}
              label={"Widget Description"}
              description={
                "Please note: if you add a severity/priority to the name use the {severity_field_name} template\n and if you want the name to include the current timeScale use the  {{period}} template"
              }
              required
              labelTop
              error={
                errors?.widget_description ? errors.widget_description : null
              }
              value={value}
              onChange={(val: React.ChangeEvent<HTMLInputElement>) =>
                onChange(val)
              }
            />
          )}
          control={control}
          rules={{ required: REQUIRED_ERROR_MESSAGE }}
        />
        <Controller
          name={"widget_help_description"}
          render={({ field: { onChange, onBlur, value } }) => (
            <TextInput
              placeholder={"Widget Help Description"}
              type={"text"}
              label={"Widget Help Description"}
              description={
                "Please note: if you add a severity/priority to the name use the {severity_field_name} template\n and if you want the name to include the current timeScale use the  ${{timeScale}} template"
              }
              labelTop
              value={value}
              onChange={(val: React.ChangeEvent<HTMLInputElement>) =>
                onChange(val)
              }
            />
          )}
          control={control}
        />
        <div className="min-max">
          <Controller
            name={"min_width"}
            render={({ field: { onChange, onBlur, value } }) => (
              <TextInput
                placeholder={"Widget Min Width (0-12)"}
                type={"number"}
                label={"Min Width"}
                labelTop
                required
                value={value}
                onChange={(val: React.ChangeEvent<HTMLInputElement>) =>
                  onChange(val)
                }
              />
            )}
            control={control}
            rules={{
              required: REQUIRED_ERROR_MESSAGE,
              max: 12,
              min: 0,
              validate: {
                validChildren: (value) => {
                  return (
                    parseFloat(value) <= parseFloat(maxWidth) ||
                    "Min Width should be less than Max Width"
                  );
                },
              },
            }}
          />
          <Controller
            name={"max_width"}
            render={({ field: { onChange, onBlur, value } }) => (
              <TextInput
                placeholder={"Widget Max Width (0-12)"}
                type={"number"}
                label={"Max Width"}
                labelTop
                required
                value={value}
                onChange={(val: React.ChangeEvent<HTMLInputElement>) =>
                  onChange(val)
                }
              />
            )}
            control={control}
            rules={{
              required: REQUIRED_ERROR_MESSAGE,
              max: 12,
              min: 0,
              validate: {
                validChildren: (value) => {
                  return (
                    parseFloat(value) >= parseFloat(minWidth) ||
                    "Min Width should be less than Min Width"
                  );
                },
              },
            }}
          />
          <Controller
            name={"min_height"}
            render={({ field: { onChange, onBlur, value } }) => (
              <TextInput
                placeholder={"Widget Min Height"}
                type={"number"}
                label={"Min Height"}
                required
                labelTop
                value={value}
                onChange={(val: React.ChangeEvent<HTMLInputElement>) =>
                  onChange(val)
                }
              />
            )}
            control={control}
            rules={{
              required: REQUIRED_ERROR_MESSAGE,
              min: 28,
            }}
          />
        </div>
        <div className="boolean-fields">
          <Controller
            name={`has_time_controller`}
            render={({ field: { value, onChange } }) => {
              return (
                <CheckBox
                  onChange={(val, newValue) => {
                    onChange(newValue);
                  }}
                  checked={value}
                  label={"Has Time Controller"}
                />
              );
            }}
            control={control}
          />
          <Controller
            name={`is_clickable`}
            render={({ field: { value, onChange } }) => {
              return (
                <CheckBox
                  onChange={(val, newValue) => {
                    onChange(newValue);
                  }}
                  checked={value}
                  label={"Is Clickable"}
                />
              );
            }}
            control={control}
          />
          <Controller
            name={`is_common`}
            render={({ field: { value, onChange } }) => {
              return (
                <CheckBox
                  onChange={(val, newValue) => {
                    onChange(newValue);
                  }}
                  checked={value}
                  label={"Is Common"}
                />
              );
            }}
            control={control}
          />
          <Controller
            name={`vertical_legend`}
            render={({ field: { value, onChange } }) => {
              return (
                <CheckBox
                  onChange={(val, newValue) => {
                    onChange(newValue);
                  }}
                  checked={value}
                  label={"Has Vertical Legend"}
                />
              );
            }}
            control={control}
          />
          <Controller
            name={`sla_widget`}
            render={({ field: { value, onChange } }) => {
              return (
                <CheckBox
                  onChange={(val, newValue) => {
                    onChange(newValue);
                  }}
                  checked={value}
                  label={"Is SLA Widget"}
                />
              );
            }}
            control={control}
          />

          <Controller
            name={`due_date_widget`}
            render={({ field: { value, onChange } }) => {
              return (
                <CheckBox
                  onChange={(val, newValue) => {
                    onChange(newValue);
                  }}
                  checked={value}
                  label={"Is Due Date Widget"}
                />
              );
            }}
            control={control}
          />
        </div>
      </div>
      <ModalButtonsContainer>
        <TextButton
          type="button"
          data-testid={"launch-action-cancel"}
          label={"Cancel"}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            closeModal();
          }}
        />
        <TextButton
          type="submit"
          label={"Launch action"}
          data-testid={"launch-action"}
          buttonStyle={"primary"}
          disabled={!isValid || !isDirty}
        />
      </ModalButtonsContainer>
    </form>
  );
};

export default AddWidgetDefinitionAction;
